'use client';

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { Offer } from '@/lib/offer-service/types/offer';
import { ReferenceTariffType } from '../types/reference-tariff-type';
import { getOffer } from '@/lib/platform/actions/get-offer';
import { CustomerType } from '@/lib/offer-service/types/customer-type';
import useEnergyCalculatorStore, {
  calculationSelector,
} from '@/app/store/use-energy-calculator-store';
import useInvoiceUploadStore from '@/lib/invoice-upload/store/use-invoice-upload-store';
import usePromoCodeStore, {
  validPromoCodeSelector,
} from '@/app/store/use-promo-code-store';
import useCustomerStore from '@/app/store/use-customer-store';

// Business offers come back with taxRate: 0 while still exposing
// gross prices, so derive the effective VAT rate from the net/gross per-kWh
// pair when taxRate is not provided.
export const getOfferTaxRate = (offer: Offer | null | undefined): number => {
  if (!offer) return 0;
  if (offer.taxRate > 0) return offer.taxRate;

  const { pricePerkWh, pricePerkWhInclTax } = offer.netCosts;
  return pricePerkWh > 0 &&
    pricePerkWhInclTax &&
    pricePerkWhInclTax > pricePerkWh
    ? pricePerkWhInclTax / pricePerkWh - 1
    : 0;
};

export interface OfferComparisonState {
  isLoading: boolean;
  currentTariffCost: number | null; // invoice cost uploaded by user
  offer: Offer | null;
  averageCost: number | null;
  gridCost: number;
  mode: ReferenceTariffType;
  offerError: 'notFound' | null;
  setCurrentTariffCost: (value: number | null) => void;
  setOffer: (value: Offer) => void;
  setGridCosts: (value: number) => void;
  setAverageCost: (value: number) => void;
  setMode: (value: ReferenceTariffType) => void;
  setIsLoading: (value: boolean) => void;
  setOfferError: (value: 'notFound' | null) => void;
}

const useOfferComparisonStore = create<OfferComparisonState>()(
  devtools(
    subscribeWithSelector((set) => ({
      isLoading: false,
      currentTariffCost: null,
      offer: null,
      averageCost: null,
      gridCost: 0,
      mode: ReferenceTariffType.average,
      offerError: null,
      setCurrentTariffCost: (value: number | null) =>
        set(
          { currentTariffCost: value },
          undefined,
          'offerComparison/setCurrentTariffCost'
        ),
      setOffer: (value: Offer) =>
        set({ offer: value }, undefined, 'offerComparison/setOffer'),
      setAverageCost: (value: number | null) =>
        set(
          { averageCost: value },
          undefined,
          'offerComparison/setAverageCost'
        ),
      setGridCosts: (value: number) =>
        set({ gridCost: value }, undefined, 'offerComparison/gridCost'),
      setMode: (value: ReferenceTariffType) => {
        set({ mode: value }, undefined, 'offerComparison/setMode');

        if (value === ReferenceTariffType.average) {
          set(
            { currentTariffCost: null },
            undefined,
            'offerComparison/setCurrentTariffCost'
          );
          useInvoiceUploadStore.getState().reset();
        } else {
          set(
            { averageCost: null },
            undefined,
            'offerComparison/setAverageCost'
          );
        }
      },
      setIsLoading: (isLoading: boolean) =>
        set({ isLoading }, undefined, 'offerComparison/setIsLoading'),
      setOfferError: (offerError: 'notFound' | null) =>
        set({ offerError }, undefined, 'offerComparison/setOfferError'),
    }))
  )
);
usePromoCodeStore.subscribe(validPromoCodeSelector, fetchAndUpdateOffer);

export async function fetchAndUpdateOffer() {
  const offerComparisonState = useOfferComparisonStore.getState();
  const energyCalculatorState = useEnergyCalculatorStore.getState();
  const { isGermanCustomer } = useCustomerStore.getState();
  const promoCode = validPromoCodeSelector(usePromoCodeStore.getState());
  const calculationData = calculationSelector(
    useEnergyCalculatorStore.getState()
  );
  const requiredFields = isGermanCustomer
    ? [
        calculationData.consumption,
        calculationData.zipCode,
        calculationData.city,
        calculationData.calculationMode,
      ]
    : [
        calculationData.consumption,
        calculationData.zipCode,
        calculationData.gridOperatorId,
        calculationData.gridAreaId,
        calculationData.calculationMode,
      ];
  if (!requiredFields.every(Boolean)) {
    return;
  }

  offerComparisonState.setIsLoading(true);
  offerComparisonState.setOfferError(null);
  try {
    const response = await getOffer({
      promoCode: promoCode,
      isGerman: isGermanCustomer,
      consumption: calculationData.consumption!,
      zipCode: calculationData.zipCode!,
      city: calculationData.city,
      street: calculationData.street,
      houseNumber: calculationData.houseNumber,
      customerType: energyCalculatorState.customerType,
      gridOperatorId: calculationData.gridOperatorId!,
      gridAreaId: calculationData.gridAreaId!,
      includeAverageTariffsPrice:
        calculationData.calculationMode === ReferenceTariffType.average,
    });
    if (response.error) {
      console.error(response.error);
      if (response.status === 404) {
        offerComparisonState.setOfferError('notFound');
      }
      throw new Error(response.error.message ?? 'Unknown offer error');
    }

    if (response.data) {
      offerComparisonState.setOffer(response.data.offer);

      if (response.data.market === 'default') {
        offerComparisonState.setGridCosts(response.data.gridCost);

        if (response.data.netAverageCost) {
          offerComparisonState.setAverageCost(response.data.netAverageCost);
        }
      }
      offerComparisonState.setMode(calculationData.calculationMode!);
    }
  } catch (err) {
    console.error(err);
    // logger.error
  }
  offerComparisonState.setIsLoading(false);
}

export const priceSelector = (state: OfferComparisonState, price: number) => {
  const energyCalculatorState = useEnergyCalculatorStore.getState();
  const customerType = energyCalculatorState.customerType;
  return customerType === CustomerType.Home
    ? applyVAT(price, getOfferTaxRate(state.offer))
    : price;
};
const applyVAT = (price: number, taxRate?: number) =>
  price * ((taxRate ?? 0) + 1);

export const selectTaxRate = (state: OfferComparisonState) =>
  getOfferTaxRate(state.offer);

export const getTaxAdjustedPromoValue = (
  promo: { value: number } | null | undefined,
  taxRate: number,
  isPrivateCustomer: boolean
) => {
  if (!promo || promo.value <= 0) return 0;
  return isPrivateCustomer ? promo.value : promo.value / (1 + taxRate);
};

export const selectPromoValue = (state: OfferComparisonState) => {
  const promo = state.offer?.promoDiscount;

  const customerType = useEnergyCalculatorStore.getState().customerType;
  const isPrivateCustomer = customerType === CustomerType.Home;
  const taxRate = getOfferTaxRate(state.offer);
  return getTaxAdjustedPromoValue(promo, taxRate, isPrivateCustomer);
};

export const selectSavingsAmount = (state: OfferComparisonState) => {
  if (!state.offer) {
    return null;
  }

  const adjustPriceForCustomerType = priceSelector.bind(undefined, state);

  if (state.mode === ReferenceTariffType.average) {
    return null;
  }

  if (state.mode === ReferenceTariffType.invoice && state.currentTariffCost) {
    return Math.floor(
      adjustPriceForCustomerType(state.currentTariffCost) -
        (computeCostsInclPromo(state) ?? 0)
    );
  }

  return null;
};
const computeCostsInclPromo = (
  state: OfferComparisonState,
  withGridCosts: boolean = false
) => {
  if (!state.offer) return null;

  const { totalEnergyCostsInclDiscounts, bonus, totalAnnualCostInclDiscounts } =
    state.offer.netCosts;
  const energyCosts =
    totalEnergyCostsInclDiscounts ?? totalAnnualCostInclDiscounts;
  const customerType = useEnergyCalculatorStore.getState().customerType;
  const isPrivateCustomer = customerType === CustomerType.Home;
  const taxRate = getOfferTaxRate(state.offer);
  const adjustedBonus = getTaxAdjustedPromoValue(
    bonus > 0 ? { value: bonus } : null,
    taxRate,
    isPrivateCustomer
  );
  return (
    priceSelector(
      state,
      withGridCosts ? totalAnnualCostInclDiscounts : energyCosts
    ) - adjustedBonus
  );
};
export const selectTotalEnergyCostsInclPromo = (state: OfferComparisonState) =>
  computeCostsInclPromo(state);

export const selectTotalAnnualCostsInclPromo = (state: OfferComparisonState) =>
  computeCostsInclPromo(state, true);

export default useOfferComparisonStore;
